import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  split,
  Observable,
  Operation,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import config from '../config'
import { APP_PATHS } from '../routes/paths'
import fragmentTypes from './generated/fragmentTypes.json'
import { globalLoadingData } from './local'

const uri = `${config.apiUrl}/graphql`
const subscriptionsUri = config.subscriptionsUri

const httpLink = new HttpLink({
  uri,
  credentials: 'include',
})

const wsUri = subscriptionsUri || ''
const wsLink = new GraphQLWsLink(createClient({ url: wsUri, lazy: true }))

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  httpLink,
)

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      // eslint-disable-next-line no-console
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    })
  }

  if (networkError) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { statusCode } = networkError as any
    // eslint-disable-next-line no-console
    console.error(`[Network error]: ${statusCode}`)
    if (statusCode === 401) {
      const { hostname, host, pathname, search, protocol, port } = window.location
      if (
        pathname !== APP_PATHS.auth.login &&
        pathname !== APP_PATHS.auth.root &&
        pathname !== APP_PATHS.root &&
        !pathname.startsWith('/invitation/')
      ) {
        window.location.href = `${protocol}//${hostname || host}${port ? `:${port}` : ''}${
          APP_PATHS.auth.login
        }?return=${encodeURIComponent(`${pathname}${search}`)}`
      }
    }
  }
})

const isTrackedOperation = (operation: Operation) => {
  if (operation.operationName === 'IntrospectionQuery') return false
  const opDefs = operation.query.definitions.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (def: any) => def.kind === 'OperationDefinition',
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return opDefs.some((def: any) => def.operation === 'mutation' || def.operation === 'query')
}

let activeRequests = 0
let showTimeout: NodeJS.Timeout | null = null
let loaderShownAt: number | null = null

const SHOW_DELAY = 500
const MIN_VISIBLE_DURATION = 900

const loaderLink = new ApolloLink((operation, forward) => {
  const shouldTrack = isTrackedOperation(operation)
  if (!shouldTrack || !forward) return forward(operation)

  if (activeRequests === 0) {
    showTimeout = setTimeout(() => {
      if (!globalLoadingData()) {
        globalLoadingData(true)
        loaderShownAt = Date.now()
      }
    }, SHOW_DELAY)
  }

  activeRequests++

  return new Observable((observer) => {
    const sub = forward(operation).subscribe({
      next: (result) => observer.next(result),
      error: (err) => observer.error(err),
      complete: () => observer.complete(),
    })

    return () => {
      sub.unsubscribe()
      activeRequests--

      if (activeRequests === 0) {
        if (showTimeout) {
          clearTimeout(showTimeout)
          showTimeout = null
        }

        const now = Date.now()
        if (loaderShownAt) {
          const elapsed = now - loaderShownAt
          const remaining = Math.max(0, MIN_VISIBLE_DURATION - elapsed)

          setTimeout(() => {
            globalLoadingData(false)
            loaderShownAt = null
          }, remaining)
        }
      }
    }
  })
})

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getApolloClient = () => {
  const cache: InMemoryCache = new InMemoryCache({ possibleTypes: fragmentTypes.possibleTypes })
  return new ApolloClient({
    link: ApolloLink.from([loaderLink, errorLink, splitLink]),
    cache,
  })
}
